import React, { useState } from 'react';
import styled from 'styled-components';
import { FiDownload, FiUpload, FiFileText, FiDatabase, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import apiService from '../../services/api';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: clamp(1.5rem, 2.5vw + 1rem, 2rem);
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Section = styled.div`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.25rem, 2vw + 1rem, 1.5rem);
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 0.875rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  min-height: 44px;
  touch-action: manipulation;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
    justify-content: center;
    padding: 1rem;
  }

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);

    &:hover {
      background: var(--primary-dark);
    }
  }

  &.secondary {
    background: var(--text-muted);
    color: var(--text-inverse);

    &:hover {
      background: var(--text-secondary);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.95rem;
  width: 100%;
  max-width: 300px;
  margin-bottom: 1rem;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.95rem;
  width: 100%;
  max-width: 300px;
  margin-bottom: 1rem;
  background: var(--background-primary);
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: var(--primary-color);
  color: var(--text-inverse);
  border-radius: 0;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background: var(--primary-dark);
  }
`;

const Message = styled.div`
  padding: 1rem;
  border-radius: 0;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.success {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border: 1px solid var(--success-color);
  }

  &.error {
    background: rgba(252, 129, 129, 0.1);
    color: var(--error-dark);
    border: 1px solid var(--error-color);
  }

  &.info {
    background: rgba(96, 165, 250, 0.1);
    color: var(--info-dark);
    border: 1px solid var(--info-color);
  }
`;

const InfoText = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-top: 0.5rem;
  line-height: 1.6;
`;

function ImportExport() {
  const [exportQuizId, setExportQuizId] = useState('');
  const [exportAssignmentId, setExportAssignmentId] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');
  const [importFile, setImportFile] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleExportQuizAttempts = async () => {
    if (!exportQuizId && !exportAssignmentId) {
      setMessage({ type: 'error', text: 'Please provide either a Quiz ID or Assignment ID' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const params = new URLSearchParams();
      if (exportQuizId) params.append('quizId', exportQuizId);
      if (exportAssignmentId) params.append('assignmentId', exportAssignmentId);
      params.append('format', exportFormat);

      const response = await fetch(`${apiService.baseURL}/export/quiz-attempts?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('faithinsight_token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Export failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'quiz-attempts-export';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setMessage({ type: 'success', text: 'Quiz attempts exported successfully!' });
    } catch (error) {
      console.error('Export error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to export quiz attempts' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportAssignmentSubmissions = async () => {
    if (!exportAssignmentId) {
      setMessage({ type: 'error', text: 'Please provide an Assignment ID' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const params = new URLSearchParams();
      params.append('assignmentId', exportAssignmentId);
      params.append('format', exportFormat);

      const response = await fetch(`${apiService.baseURL}/export/assignment-submissions?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('faithinsight_token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Export failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'assignment-submissions-export';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setMessage({ type: 'success', text: 'Assignment submissions exported successfully!' });
    } catch (error) {
      console.error('Export error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to export assignment submissions' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportQuizMigration = async () => {
    if (!exportQuizId) {
      setMessage({ type: 'error', text: 'Please provide a Quiz ID' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const params = new URLSearchParams();
      params.append('quizId', exportQuizId);

      const response = await fetch(`${apiService.baseURL}/export/quiz-migration?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('faithinsight_token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Export failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'quiz-migration-export.json';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setMessage({ type: 'success', text: 'Quiz migration data exported successfully!' });
    } catch (error) {
      console.error('Export error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to export quiz migration data' });
    } finally {
      setLoading(false);
    }
  };

  const handleImportQuiz = async () => {
    if (!importFile) {
      setMessage({ type: 'error', text: 'Please select a JSON file to import' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      // Read file
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(importFile);
      });

      // Parse JSON
      const importData = JSON.parse(fileContent);

      // Import via API
      const response = await apiService.request('/export/quiz-import', {
        method: 'POST',
        body: JSON.stringify(importData)
      });

      if (response.success) {
        setMessage({ type: 'success', text: `Quiz imported successfully! New Quiz ID: ${response.data.quiz_id}` });
        setImportFile(null);
        // Reset file input
        const fileInput = document.getElementById('import-file');
        if (fileInput) fileInput.value = '';
      } else {
        throw new Error(response.message || 'Import failed');
      }
    } catch (error) {
      console.error('Import error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to import quiz. Please check the file format.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>
          <FiDownload />
          Import/Export Data
        </Title>
      </Header>

      {message && (
        <Message className={message.type}>
          {message.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
          {message.text}
        </Message>
      )}

      {/* Export Section */}
      <Section>
        <SectionTitle>
          <FiDownload />
          Export Data
        </SectionTitle>

        <div style={{ marginBottom: '2rem' }}>
          <Label>Quiz ID (optional, for quiz attempts export)</Label>
          <Input
            type="number"
            value={exportQuizId}
            onChange={(e) => setExportQuizId(e.target.value)}
            placeholder="Enter Quiz ID"
          />
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <Label>Assignment ID (for quiz attempts or assignment submissions)</Label>
          <Input
            type="number"
            value={exportAssignmentId}
            onChange={(e) => setExportAssignmentId(e.target.value)}
            placeholder="Enter Assignment ID"
          />
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <Label>Export Format</Label>
          <Select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
            <option value="csv">CSV</option>
            <option value="excel">Excel (XLSX)</option>
            <option value="json">JSON</option>
          </Select>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <Button
            className="primary"
            onClick={handleExportQuizAttempts}
            disabled={loading || (!exportQuizId && !exportAssignmentId)}
          >
            <FiFileText />
            Export Quiz Attempts
          </Button>

          <Button
            className="primary"
            onClick={handleExportAssignmentSubmissions}
            disabled={loading || !exportAssignmentId}
          >
            <FiFileText />
            Export Assignment Submissions
          </Button>

          <Button
            className="primary"
            onClick={handleExportQuizMigration}
            disabled={loading || !exportQuizId}
          >
            <FiDatabase />
            Export Quiz for Migration (JSON)
          </Button>
        </div>

        <InfoText>
          <strong>Export Quiz Attempts:</strong> Exports all quiz attempts and responses in the selected format.
          Requires either Quiz ID or Assignment ID.
          <br />
          <strong>Export Assignment Submissions:</strong> Exports all submissions for an assignment.
          Requires Assignment ID.
          <br />
          <strong>Export Quiz for Migration:</strong> Exports complete quiz data (questions, options, assignments) in JSON format for migration purposes.
        </InfoText>
      </Section>

      {/* Import Section */}
      <Section>
        <SectionTitle>
          <FiUpload />
          Import Data
        </SectionTitle>

        <div style={{ marginBottom: '2rem' }}>
          <Label>Import Quiz from JSON</Label>
          <div>
            <FileInput
              id="import-file"
              type="file"
              accept=".json"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
            <FileInputLabel htmlFor="import-file">
              <FiUpload />
              {importFile ? importFile.name : 'Choose JSON File'}
            </FileInputLabel>
          </div>
        </div>

        <Button
          className="secondary"
          onClick={handleImportQuiz}
          disabled={loading || !importFile}
        >
          <FiUpload />
          Import Quiz
        </Button>

        <InfoText>
          <strong>Import Quiz:</strong> Import a quiz from a JSON file exported using the "Export Quiz for Migration" feature.
          The imported quiz will be created as a new quiz with all questions and options.
        </InfoText>
      </Section>
    </Container>
  );
}

export default ImportExport;
